import React, { forwardRef } from 'react';
import { Form } from './final-form/react';
import AutoSaveSpy from './AutoSaveSpy';

/**
 * A smarter replacement for keepDirtyOnReinitialize
 * that avoids overwriting any fields the user it working on.
 * Internal logic based on https://github.com/final-form/final-form/blob/d20c44be8766b93424e7754fe2629820fd93d21a/src/FinalForm.js#L782
 */
export const AutoSaveMutator = ([data], mutableState, { getIn, setIn }) => {
  // Make copies, just in case
  const formState = { ...mutableState.formState };
  const fields = { ...mutableState.fields };

  const dataValues = typeof data === 'function' ? data({ ...formState.values }) : { ...data };
  let workingValues = { ...dataValues };

  // Keep values that will not match the new initialValues,
  // and were either modified or did not match the previous initialValues.
  Object.entries(fields).forEach(([key, field]) => {
    const value = getIn(formState.values, key);
    const pristine = field.isEqual(
      value,
      getIn(formState.initialValues || {}, key),
    );
    const { modified } = field;
    const willBePristine = field.isEqual(
      value,
      getIn(dataValues || {}, key),
    );
    if (!willBePristine && (!pristine || modified)) {
      workingValues = setIn(workingValues, key, value) || {};
    } else {
      // AutoSave forms don't use submit, so we have to revert this ourselves
      // eslint-disable-next-line no-param-reassign
      mutableState.fields[key].modified = false;
    }
  });
  // Write in the new values
  /* eslint-disable no-param-reassign */
  mutableState.formState.initialValues = { ...dataValues };
  mutableState.formState.values = workingValues;
  /* eslint-enable no-param-reassign */
};

const AutoSaveDecorator = (form) => {
  const originalInitialize = form.initialize;
  // eslint-disable-next-line no-param-reassign
  form.initialize = (data) => {
    form.mutators.AutoSaveMutator(data);
  };

  // Undecorate
  return () => {
    // eslint-disable-next-line no-param-reassign
    form.initialize = originalInitialize;
  };
};

const AutoSaveForm = forwardRef((props, ref) => {
  const {
    initialValues,
    onFormChange,
    saveDelay = 300,
    mutators = {},
    decorators = [],
    keepDirtyOnReinitialize, // functionality replaced/improved by AutoSave Decorator/Mutator pair
    onSubmit, // unused for AutoSave purposes
    children,
    ...formProps
  } = props;
  return (
    <Form
      ref={ref}
      initialValues={initialValues}
      onSubmit={() => true}
      {...formProps}
      mutators={{ ...mutators, AutoSaveMutator }}
      decorators={[AutoSaveDecorator, ...decorators]}
    >
      {(renderProps) => (
        <>
          <AutoSaveSpy
            onFormChange={onFormChange}
            saveDelay={saveDelay}
          />
          {children(renderProps)}
        </>
      )}
    </Form>
  );
});

export default AutoSaveForm;
